/**
 * sRGB <-> OKLab <-> OKLCH, plus the handful of perceptual predicates the brand
 * scorer and the contrast auditor need.
 *
 * This lives in @clawharbor/manifest because it is the only package both sides
 * of the colour story already depend on. It began as two implementations:
 * brand-extract parsed hex/rgb/hsl/oklch/oklab/148 named colours to SCORE a
 * palette, and render-core parsed hex/rgb only to AUDIT one. Two WCAG luminance
 * functions, two contrast ratios, and an auditor that reported `skipped` for
 * every oklch() token the extractor was perfectly capable of reading. Collapsing
 * them here removes the fork and gives the auditor the full parser. It has no
 * dependencies and no module-scope side effects, so a browser bundle that only
 * wants `parseCssColor` tree-shakes the rest away.
 *
 * Why OKLCH and not HSL: HSL lightness is not lightness. #ffff00 and #0000ff are
 * both L=50% in HSL, which makes "deprioritise the near-neutrals" impossible to
 * express. In OKLCH, chroma really is colourfulness, so the single most important
 * rule in this extractor -- a brand colour is chromatic, white/black/grey are not
 * -- becomes one comparison instead of a pile of special cases.
 *
 * Matrices from the Bjorn Ottosson reference implementation (public domain).
 */

export interface Rgb {
  /** 0..255 */
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

export interface Oklab {
  L: number;
  a: number;
  b: number;
}

export interface Oklch {
  /** 0..1 */
  L: number;
  /** 0..~0.4 inside the sRGB gamut */
  C: number;
  /** degrees, 0..360 */
  h: number;
}

/** A colour we have fully understood: canonical text form plus perceptual coords. */
export interface Color {
  /** Canonical CSS form: #rrggbb, or rgba(...) when alpha < 1. */
  css: string;
  hex: string;
  rgb: Rgb;
  oklch: Oklch;
  /** CSS oklch() serialisation, for ColorToken.oklch. */
  oklchCss: string;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/* ------------------------------------------------------------------ parsing */

// The full CSS named-colour table. Real stylesheets say "white" and "transparent"
// far more often than they say #ffffff.
const NAMED_RAW =
  'aliceblue f0f8ff,antiquewhite faebd7,aqua 00ffff,aquamarine 7fffd4,azure f0ffff,beige f5f5dc,' +
  'bisque ffe4c4,black 000000,blanchedalmond ffebcd,blue 0000ff,blueviolet 8a2be2,brown a52a2a,' +
  'burlywood deb887,cadetblue 5f9ea0,chartreuse 7fff00,chocolate d2691e,coral ff7f50,' +
  'cornflowerblue 6495ed,cornsilk fff8dc,crimson dc143c,cyan 00ffff,darkblue 00008b,' +
  'darkcyan 008b8b,darkgoldenrod b8860b,darkgray a9a9a9,darkgreen 006400,darkgrey a9a9a9,' +
  'darkkhaki bdb76b,darkmagenta 8b008b,darkolivegreen 556b2f,darkorange ff8c00,darkorchid 9932cc,' +
  'darkred 8b0000,darksalmon e9967a,darkseagreen 8fbc8f,darkslateblue 483d8b,darkslategray 2f4f4f,' +
  'darkslategrey 2f4f4f,darkturquoise 00ced1,darkviolet 9400d3,deeppink ff1493,deepskyblue 00bfff,' +
  'dimgray 696969,dimgrey 696969,dodgerblue 1e90ff,firebrick b22222,floralwhite fffaf0,' +
  'forestgreen 228b22,fuchsia ff00ff,gainsboro dcdcdc,ghostwhite f8f8ff,gold ffd700,' +
  'goldenrod daa520,gray 808080,green 008000,greenyellow adff2f,grey 808080,honeydew f0fff0,' +
  'hotpink ff69b4,indianred cd5c5c,indigo 4b0082,ivory fffff0,khaki f0e68c,lavender e6e6fa,' +
  'lavenderblush fff0f5,lawngreen 7cfc00,lemonchiffon fffacd,lightblue add8e6,lightcoral f08080,' +
  'lightcyan e0ffff,lightgoldenrodyellow fafad2,lightgray d3d3d3,lightgreen 90ee90,' +
  'lightgrey d3d3d3,lightpink ffb6c1,lightsalmon ffa07a,lightseagreen 20b2aa,lightskyblue 87cefa,' +
  'lightslategray 778899,lightslategrey 778899,lightsteelblue b0c4de,lightyellow ffffe0,' +
  'lime 00ff00,limegreen 32cd32,linen faf0e6,magenta ff00ff,maroon 800000,mediumaquamarine 66cdaa,' +
  'mediumblue 0000cd,mediumorchid ba55d3,mediumpurple 9370db,mediumseagreen 3cb371,' +
  'mediumslateblue 7b68ee,mediumspringgreen 00fa9a,mediumturquoise 48d1cc,mediumvioletred c71585,' +
  'midnightblue 191970,mintcream f5fffa,mistyrose ffe4e1,moccasin ffe4b5,navajowhite ffdead,' +
  'navy 000080,oldlace fdf5e6,olive 808000,olivedrab 6b8e23,orange ffa500,orangered ff4500,' +
  'orchid da70d6,palegoldenrod eee8aa,palegreen 98fb98,paleturquoise afeeee,palevioletred db7093,' +
  'papayawhip ffefd5,peachpuff ffdab9,peru cd853f,pink ffc0cb,plum dda0dd,powderblue b0e0e6,' +
  'purple 800080,rebeccapurple 663399,red ff0000,rosybrown bc8f8f,royalblue 4169e1,' +
  'saddlebrown 8b4513,salmon fa8072,sandybrown f4a460,seagreen 2e8b57,seashell fff5ee,' +
  'sienna a0522d,silver c0c0c0,skyblue 87ceeb,slateblue 6a5acd,slategray 708090,slategrey 708090,' +
  'snow fffafa,springgreen 00ff7f,steelblue 4682b4,tan d2b48c,teal 008080,thistle d8bfd8,' +
  'tomato ff6347,turquoise 40e0d0,violet ee82ee,wheat f5deb3,white ffffff,whitesmoke f5f5f5,' +
  'yellow ffff00,yellowgreen 9acd32';

const NAMED: Map<string, string> = new Map(
  NAMED_RAW.split(',').map((pair) => {
    const parts = pair.trim().split(' ');
    return [parts[0] ?? '', parts[1] ?? '000000'] as [string, string];
  }),
);

function hexToRgb(hex: string): Rgb | null {
  const h = hex.replace('#', '').trim();
  if (!/^[0-9a-fA-F]+$/.test(h)) return null;
  const expand = (s: string): number => parseInt(s.length === 1 ? s + s : s, 16);
  if (h.length === 3 || h.length === 4) {
    const r = h[0];
    const g = h[1];
    const b = h[2];
    const a = h[3];
    if (r == null || g == null || b == null) return null;
    return { r: expand(r), g: expand(g), b: expand(b), a: a == null ? 1 : expand(a) / 255 };
  }
  if (h.length === 6 || h.length === 8) {
    const n = (i: number): number => parseInt(h.slice(i, i + 2), 16);
    return { r: n(0), g: n(2), b: n(4), a: h.length === 8 ? n(6) / 255 : 1 };
  }
  return null;
}

/** Split "1 2 3 / 0.5" or "1, 2, 3, 0.5" into raw tokens. */
function splitArgs(inner: string): string[] {
  return inner
    .replace(/\//g, ' ')
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** rgb() channel: percentage maps onto 0..255, bare number is already 0..255. */
function channel(tok: string | undefined): number {
  if (tok == null || tok === 'none') return 0;
  if (tok.endsWith('%')) return (parseFloat(tok) / 100) * 255;
  const v = parseFloat(tok);
  return Number.isFinite(v) ? v : 0;
}

/** hsl() saturation/lightness: percentage or 0..100 number, normalised to 0..1. */
function ratio01(tok: string | undefined): number {
  if (tok == null || tok === 'none') return 0;
  const v = parseFloat(tok);
  if (!Number.isFinite(v)) return 0;
  if (tok.endsWith('%')) return clamp(v / 100, 0, 1);
  return clamp(v > 1 ? v / 100 : v, 0, 1);
}

function alphaOf(tok: string | undefined): number {
  if (tok == null || tok === 'none') return 1;
  const v = tok.endsWith('%') ? parseFloat(tok) / 100 : parseFloat(tok);
  return Number.isFinite(v) ? clamp(v, 0, 1) : 1;
}

function hslToRgb(h: number, s: number, l: number, a: number): Rgb {
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = l - c / 2;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (hh < 60) {
    rp = c;
    gp = x;
  } else if (hh < 120) {
    rp = x;
    gp = c;
  } else if (hh < 180) {
    gp = c;
    bp = x;
  } else if (hh < 240) {
    gp = x;
    bp = c;
  } else if (hh < 300) {
    rp = x;
    bp = c;
  } else {
    rp = c;
    bp = x;
  }
  return { r: (rp + m) * 255, g: (gp + m) * 255, b: (bp + m) * 255, a };
}

/**
 * Parse any colour form that realistically appears in a stylesheet or a computed
 * style. Returns null -- never a guess -- for transparent, currentColor, var(),
 * gradients and unsupported colour spaces. A fabricated brand colour is worse
 * than a missing one.
 */
export function parseCssColor(raw: string): Rgb | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v === 'transparent' || v === 'currentcolor' || v === 'inherit' || v === 'none') return null;
  if (v.startsWith('var(') || v.includes('gradient(')) return null;
  if (v.startsWith('#')) return hexToRgb(v);

  const named = NAMED.get(v);
  if (named != null) return hexToRgb(named);

  const fn = /^([a-z-]+)\((.*)\)$/s.exec(v);
  if (fn == null) return null;
  const name = fn[1] ?? '';
  const args = splitArgs(fn[2] ?? '');

  if (name === 'rgb' || name === 'rgba') {
    return {
      r: clamp(channel(args[0]), 0, 255),
      g: clamp(channel(args[1]), 0, 255),
      b: clamp(channel(args[2]), 0, 255),
      a: alphaOf(args[3]),
    };
  }
  if (name === 'hsl' || name === 'hsla') {
    return hslToRgb(parseFloat(args[0] ?? '0') || 0, ratio01(args[1]), ratio01(args[2]), alphaOf(args[3]));
  }
  if (name === 'oklch') {
    const L = ratio01(args[0]);
    const C = parseFloat(args[1] ?? '0');
    const h = parseFloat(args[2] ?? '0');
    if (!Number.isFinite(C) || !Number.isFinite(h)) return null;
    const rgb = oklchToRgb({ L, C, h });
    rgb.a = alphaOf(args[3]);
    return rgb;
  }
  if (name === 'oklab') {
    const rgb = oklabToRgb({
      L: ratio01(args[0]),
      a: parseFloat(args[1] ?? '0') || 0,
      b: parseFloat(args[2] ?? '0') || 0,
    });
    rgb.a = alphaOf(args[3]);
    return rgb;
  }
  return null;
}

/* -------------------------------------------------------------- conversions */

const srgbToLinear = (c: number): number => {
  const x = c / 255;
  return x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
};

const linearToSrgb = (x: number): number => {
  const v = x <= 0.0031308 ? x * 12.92 : 1.055 * Math.pow(Math.max(x, 0), 1 / 2.4) - 0.055;
  return clamp(Math.round(v * 255), 0, 255);
};

export function rgbToOklab(rgb: Rgb): Oklab {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);

  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const lc = Math.cbrt(l);
  const mc = Math.cbrt(m);
  const sc = Math.cbrt(s);

  return {
    L: 0.2104542553 * lc + 0.793617785 * mc - 0.0040720468 * sc,
    a: 1.9779984951 * lc - 2.428592205 * mc + 0.4505937099 * sc,
    b: 0.0259040371 * lc + 0.7827717662 * mc - 0.808675766 * sc,
  };
}

export function oklabToRgb(lab: Oklab): Rgb {
  const lc = lab.L + 0.3963377774 * lab.a + 0.2158037573 * lab.b;
  const mc = lab.L - 0.1055613458 * lab.a - 0.0638541728 * lab.b;
  const sc = lab.L - 0.0894841775 * lab.a - 1.291485548 * lab.b;

  const l = lc * lc * lc;
  const m = mc * mc * mc;
  const s = sc * sc * sc;

  return {
    r: linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
    a: 1,
  };
}

export function oklabToOklch(lab: Oklab): Oklch {
  const C = Math.hypot(lab.a, lab.b);
  let h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { L: lab.L, C, h: C < 1e-6 ? 0 : h };
}

export function oklchToOklab(lch: Oklch): Oklab {
  const rad = (lch.h * Math.PI) / 180;
  return { L: lch.L, a: Math.cos(rad) * lch.C, b: Math.sin(rad) * lch.C };
}

export const rgbToOklch = (rgb: Rgb): Oklch => oklabToOklch(rgbToOklab(rgb));
export const oklchToRgb = (lch: Oklch): Rgb => oklabToRgb(oklchToOklab(lch));

/* -------------------------------------------------------------- predicates */

/**
 * Euclidean distance in OKLab: the deltaEOK of CSS Color 4. Roughly, 0.02 is
 * "the same colour with a rounding difference", 0.05 is "a deliberate second
 * shade", 0.1 is "a different colour".
 */
export function deltaEOk(a: Oklch, b: Oklch): number {
  const la = oklchToOklab(a);
  const lb = oklchToOklab(b);
  return Math.hypot(la.L - lb.L, la.a - lb.a, la.b - lb.b);
}

/** Smallest angular distance between two hues, in degrees. */
export function hueDistance(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
}

export function relativeLuminance(rgb: Rgb): number {
  return 0.2126 * srgbToLinear(rgb.r) + 0.7152 * srgbToLinear(rgb.g) + 0.0722 * srgbToLinear(rgb.b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** True for white, black and every grey between them. */
export const isNeutral = (lch: Oklch, threshold = 0.035): boolean => lch.C < threshold;

/* -------------------------------------------------------------- formatting */

export function toHex(rgb: Rgb): string {
  const h = (v: number): string => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0');
  return `#${h(rgb.r)}${h(rgb.g)}${h(rgb.b)}`;
}

const round = (v: number, dp: number): number => Math.round(v * 10 ** dp) / 10 ** dp;

export function formatOklch(lch: Oklch): string {
  return `oklch(${round(lch.L * 100, 2)}% ${round(lch.C, 4)} ${round(lch.h, 2)})`;
}

/** Bundle a parsed rgb into the canonical Color record used everywhere else. */
export function toColor(rgb: Rgb): Color {
  const oklch = rgbToOklch(rgb);
  const hex = toHex(rgb);
  const css =
    rgb.a >= 0.999
      ? hex
      : `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${round(rgb.a, 3)})`;
  return { css, hex, rgb, oklch, oklchCss: formatOklch(oklch) };
}

export function colorFrom(raw: string): Color | null {
  const rgb = parseCssColor(raw);
  return rgb == null ? null : toColor(rgb);
}

/** Composite a translucent colour over an opaque backdrop (source-over). */
export function flatten(fg: Rgb, bg: Rgb): Rgb {
  if (fg.a >= 0.999) return { ...fg, a: 1 };
  const a = fg.a;
  return {
    r: fg.r * a + bg.r * (1 - a),
    g: fg.g * a + bg.g * (1 - a),
    b: fg.b * a + bg.b * (1 - a),
    a: 1,
  };
}

/** Shift lightness in OKLCH: the honest way to derive a hover or a muted step. */
export function shiftL(c: Color, delta: number): Color {
  const rgb = oklchToRgb({ ...c.oklch, L: clamp(c.oklch.L + delta, 0, 1) });
  rgb.a = c.rgb.a;
  return toColor(rgb);
}

/** Move `from` a fraction of the way toward `to`, interpolating in OKLab. */
export function mix(from: Color, to: Color, t: number): Color {
  const a = oklchToOklab(from.oklch);
  const b = oklchToOklab(to.oklch);
  return toColor(
    oklabToRgb({ L: a.L + (b.L - a.L) * t, a: a.a + (b.a - a.a) * t, b: a.b + (b.b - a.b) * t }),
  );
}

/** Whichever of black/white is more readable on `bg`. */
export function bestContrastOn(bg: Color): Color {
  const white: Rgb = { r: 255, g: 255, b: 255, a: 1 };
  const black: Rgb = { r: 0, g: 0, b: 0, a: 1 };
  return toColor(contrastRatio(white, bg.rgb) >= contrastRatio(black, bg.rgb) ? white : black);
}
